import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Tag, Descriptions, Table, Button, Space, Modal, Input, InputNumber, Image, Popconfirm, Spinner, Result, Tooltip, Row, Col, Timeline, message,
} from '../../ui';
import { Check, X, ArrowLeft, Undo2, Send } from 'lucide-react';
import { usePermissionChecker, useAuthStore } from '../../store/auth';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { EditReportModal, EditTab } from './EditReportModal';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Report-level status chip colours.
const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'blue',
  APPROVED: 'cyan',
  CLOSED: 'green',
  CORRECTION_REQUIRED: 'orange',
  REJECTED: 'red',
};

// Per-line verification status chip colours.
const VERIFY_COLOR: Record<string, string> = {
  PENDING: 'default',
  VERIFIED: 'green',
  MISSING: 'red',
  AMOUNT_MISMATCH: 'orange',
};


const REJECTED_STATUSES = ['REJECTED'];

export function ExpenseReportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const myId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', id],
    queryFn: () => request<any>({ url: `/expense-reports/${id}` }),
    // Approval state is sensitive — always re-fetch the live status on open so we
    // never show stale action buttons for an already-progressed report.
    refetchOnMount: 'always',
  });

  // Generic PATCH action against this report (submit / approve / reject / …).
  const action = useMutation({
    mutationFn: ({ url, data }: { url: string; data?: any }) =>
      request({ url: `/expense-reports/${id}/${url}`, method: 'PATCH', data }),
    onSuccess: (_, vars) => {
      message.success(`Action "${vars.url}" complete`);
      qc.invalidateQueries({ queryKey: ['expense-report', id] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // Per-line verification.
  const verifyItem = useMutation({
    mutationFn: (vars: { itemId: string; data: any }) =>
      request({ url: `/expense-reports/${id}/items/${vars.itemId}/verify`, method: 'PATCH', data: vars.data }),
    onSuccess: () => {
      message.success('Line updated');
      qc.invalidateQueries({ queryKey: ['expense-report', id] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
      setMismatchItem(null);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // ── Modal state for reason / amount inputs ──
  const [reasonModal, setReasonModal] = useState<{ url: string; title: string; field: string } | null>(null);
  const [reason, setReason] = useState('');

  const [adminOpen, setAdminOpen] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState<number | null>(null);
  const [adminRemarks, setAdminRemarks] = useState('');

  // Mismatch verify needs a verified amount.
  const [mismatchItem, setMismatchItem] = useState<any | null>(null);
  const [verifiedAmount, setVerifiedAmount] = useState<number | null>(null);
  const [mismatchReason, setMismatchReason] = useState('');
  const [editTab, setEditTab] = useState<EditTab | null>(null);

  // Honor "?edit=1" from a deep link — open the editor once the report loads, but only for
  // someone allowed to edit (owner / filer, while draft / sent back).
  useEffect(() => {
    if (!report || searchParams.get('edit') !== '1') return;
    const owner = String(report.repId) === String(myId);
    const creator = String(report.createdBy ?? '') === String(myId);
    if ((owner || creator) && ['DRAFT', 'CORRECTION_REQUIRED'].includes(report.status)) setEditTab('expenses');
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [report, searchParams, myId, setSearchParams]);

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !report) {
    return (
      <Result
        status="error"
        title="Couldn't load this report"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/expense-reports')}>Back to reports</Button>}
      />
    );
  }

  const status: string = report.status;
  const isOwner = String(report.repId) === String(myId);
  // The manager/admin who filed on a rep's behalf isn't the owner, but still owns the draft.
  const isCreator = String(report.createdBy ?? '') === String(myId);
  const isFiler = isOwner || isCreator;

  // Permission + status gating (mirrors the backend guards).
  const isApprover = hasPerm('EXPENSE.APPROVE.HEAD') || hasPerm('EXPENSE.APPROVE.ACCT');
  const canSubmit = isFiler && (status === 'DRAFT' || status === 'CORRECTION_REQUIRED');
  // Single approval layer: any approver approves / rejects / sends back a SUBMITTED report.
  const canReview = status === 'SUBMITTED' && isApprover;
  const canClose = status === 'APPROVED' && hasPerm('EXPENSE.APPROVE.ACCT');
  // Reopen is an Admin / Super Admin override only — not a routine accounts action.
  const canReopen = (REJECTED_STATUSES.includes(status) || status === 'CLOSED') && (role === 'ADMIN' || role === 'SUPER_ADMIN');
  // The sales person (owner) or whoever filed it edits the report only while it's a
  // draft / sent back — no active wallet debit exists in those states, so it's safe.
  const canEdit = isFiler && (status === 'DRAFT' || status === 'CORRECTION_REQUIRED');
  const canEditItems = canEdit;
  // Per-line verify is open to any approver while the report is in review.
  const canVerifyLines =
    status === 'SUBMITTED' &&
    (hasPerm('EXPENSE.APPROVE.HEAD') || hasPerm('EXPENSE.APPROVE.ACCT'));

  const items: any[] = report.items ?? [];

  const setVerify = (itemId: string, vStatus: string) =>
    verifyItem.mutate({ itemId, data: { status: vStatus } });

  const lineColumns: any[] = [
    { title: 'Date', dataIndex: 'expenseDate', width: 110, render: (v: any) => formatIst(v, 'DD MMM YYYY') },
    { title: 'Category', dataIndex: 'category', width: 130, render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Vendor', dataIndex: 'vendorName', width: 130, render: (v: any) => v || '—' },
    { title: 'Bill #', dataIndex: 'billNumber', width: 110, render: (v: any) => v || '—' },
    { title: 'Mode', dataIndex: 'paymentMode', width: 110, render: (v: any) => (v ? <Tag>{v}</Tag> : '—') },
    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, width: 110, render: (v: any) => inr2(v) },
    {
      title: 'Bill',
      dataIndex: 'billUrl',
      width: 70,
      render: (url: string, r: any) =>
        url
          ? (/\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url)
            ? <Image src={url} width={36} height={36} style={{ objectFit: 'cover' }} />
            : <a href={url} target="_blank" rel="noopener">View</a>)
          : <Tag color={r.isBillAttached ? 'default' : 'red'}>—</Tag>,
    },
    {
      title: 'Verification',
      key: 'verification',
      width: 160,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={2}>
          <Tag color={VERIFY_COLOR[r.verificationStatus] ?? 'default'}>
            {(r.verificationStatus ?? 'PENDING').replace(/_/g, ' ')}
          </Tag>
          {r.verificationStatus === 'AMOUNT_MISMATCH' && r.verifiedAmount != null && (
            <span style={{ fontSize: 12, color: '#888' }}>Verified {inr2(r.verifiedAmount)}</span>
          )}
          {r.rejectionReason && (
            <span style={{ fontSize: 12, color: '#D9363E' }}>{r.rejectionReason}</span>
          )}
        </Space>
      ),
    },
    ...(canVerifyLines ? [{
      title: 'Verify',
      key: 'verifyAction',
      width: 200,
      align: 'right' as const,
      render: (_: any, r: any) => (
        <Space size={4} wrap>
          <Tooltip label="Mark verified">
            <Button
              size="sm" variant="ghost" aria-label="Mark verified" icon={<Check size={16} />}
              onClick={() => setVerify(String(r.id), 'VERIFIED')}
              loading={verifyItem.isPending && verifyItem.variables?.itemId === String(r.id)}
            />
          </Tooltip>
          <Tooltip label="Bill missing">
            <Button size="sm" variant="danger" aria-label="Bill missing" icon={<X size={16} />} onClick={() => setVerify(String(r.id), 'MISSING')} />
          </Tooltip>
          <Button
            size="sm"
            onClick={() => {
              setMismatchItem(r);
              setVerifiedAmount(Number(r.verifiedAmount) || Number(r.amount) || null);
              setMismatchReason(r.rejectionReason ?? '');
            }}
          >
            Mismatch
          </Button>
        </Space>
      ),
    }] : []),
  ];

  // Decisions: each step renders done (name + time) or a greyed "pending".
  const dPending = <Typography.Text type="secondary" italic>pending</Typography.Text>;
  const dDone = (name?: string, at?: string, color = '#1F8A4C', label = '') => (
    <span>
      <span style={{ color }}>{label}{name ? `${label ? ' ' : ''}${name}` : ''}</span>
      {at && <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>{formatIst(at)}</span>}
    </span>
  );
  const submittedCell = report.submittedAt ? dDone(report.submittedByName, report.submittedAt, '#1677FF') : dPending;
  // Single approval layer — one decision row (approved or rejected by whoever acted).
  const approvedCell = report.adminDecisionAt
    ? dDone(report.approvedByName ?? report.adminDecisionByName, report.adminDecisionAt, status === 'REJECTED' ? '#D9363E' : '#1F8A4C', status === 'REJECTED' ? 'Rejected by' : 'Approved by')
    : dPending;

  // Activity timeline — milestones derived from the report's own timestamps.
  const tinfo = (s?: string) => <span style={{ color: '#888', fontSize: 12 }}>{s}</span>;
  const activity: { color?: string; children: any }[] = [];
  if (report.createdAt) activity.push({ color: 'gray', children: <span><b>Created</b><br />{tinfo(`${report.repName ?? ''} · ${formatIst(report.createdAt)}`)}</span> });
  if (report.submittedAt) activity.push({ color: 'blue', children: <span><b>Submitted</b> {tinfo(`${report.submittedByName ?? ''} · ${formatIst(report.submittedAt)}`)}</span> });
  if (status === 'CORRECTION_REQUIRED' && report.correctionReason) activity.push({ color: 'orange', children: <span><b>Sent back</b> <span style={{ fontSize: 12 }}>{report.correctionReason}</span></span> });
  if (report.adminDecisionAt) activity.push({ color: status === 'REJECTED' ? 'red' : 'green', children: <span><b>{status === 'REJECTED' ? 'Rejected' : 'Approved'}</b> {tinfo(`${(report.approvedByName ?? report.adminDecisionByName) ?? ''} · ${formatIst(report.adminDecisionAt)}`)}{report.adminRemarks && <div style={{ fontSize: 12 }}>{report.adminRemarks}</div>}</span> });
  if (status === 'CLOSED') activity.push({ color: 'green', children: <span><b>Closed</b></span> });

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/expense-reports')}>Back</Button>
      </Space>

      {/* Header + role/status-gated actions */}
      <Card style={{ marginBottom: 16 }}>
        <Space align="center" wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space align="center" wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>{report.reportNo}</Typography.Title>
            <Tag color={STATUS_COLOR[status] ?? 'default'} style={{ fontSize: 14 }}>{(status ?? '').replace(/_/g, ' ')}</Tag>
          </Space>
          <Typography.Text type="secondary">
            <b>{report.repName ?? `#${report.repId}`}</b> · {report.visitType ?? '—'} · Filed {formatIst(report.createdAt)}
          </Typography.Text>
        </Space>

        <Space style={{ marginTop: 12 }} wrap>
          {canSubmit && (
            <Popconfirm title="Submit this report for approval?" description="It goes for approval next. You can't edit it after submitting." okText="Submit"
              onConfirm={() => action.mutate({ url: 'submit' })}>
              <Button variant="primary" icon={<Send size={16} />} loading={action.isPending}>Submit</Button>
            </Popconfirm>
          )}
          {canReview && (
            <>
              <Button variant="primary" icon={<Check size={16} />} onClick={() => { setApprovedAmount(null); setAdminRemarks(''); setAdminOpen(true); }}>Approve</Button>
              <Button variant="danger" icon={<X size={16} />} onClick={() => { setReason(''); setReasonModal({ url: 'reject', title: 'Reject report', field: 'reason' }); }}>Reject</Button>
              <Button icon={<Undo2 size={16} />} onClick={() => { setReason(''); setReasonModal({ url: 'send-back', title: 'Send back for correction', field: 'reason' }); }}>Send back</Button>
            </>
          )}
          {canClose && (
            <Popconfirm title="Close this report?" description="This finishes the report." okText="Close" onConfirm={() => action.mutate({ url: 'close' })}>
              <Button variant="primary" icon={<Check size={16} />} loading={action.isPending}>Close</Button>
            </Popconfirm>
          )}
          {canReopen && (
            <Button icon={<Undo2 size={16} />} onClick={() => { setReason(''); setReasonModal({ url: 'reopen', title: 'Reopen report', field: 'reason' }); }}>Reopen</Button>
          )}
        </Space>
      </Card>

      <Row gutter={16}>
        {/* Left: site, visit, items */}
        <Col xs={24} lg={15}>
          <Card title="Site visited" style={{ marginBottom: 16 }}
            extra={canEdit ? <Button size="sm" onClick={() => setEditTab('details')}>Edit details</Button> : undefined}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Customer">{report.partyName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Contact">{report.partyContact ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Site">{[report.partyAddress, report.city, report.state, report.pincode].filter(Boolean).join(', ') || '—'}</Descriptions.Item>
              <Descriptions.Item label="Visit type">{report.visitType ?? '—'}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Visit details" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Exit at">{report.exitAt ? formatIst(report.exitAt) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Return at">{report.returnAt ? formatIst(report.returnAt) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Travel mode">{report.travelMode ?? '—'}</Descriptions.Item>
              {report.travelStartLoc && <Descriptions.Item label="From">{report.travelStartLoc}</Descriptions.Item>}
              {report.travelEndLoc && <Descriptions.Item label="To">{report.travelEndLoc}</Descriptions.Item>}
            </Descriptions>
            {(report.workDone || report.observations || report.partyFeedback || report.notes) && (
              <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                {report.workDone && <Descriptions.Item label="Work done">{report.workDone}</Descriptions.Item>}
                {report.observations && <Descriptions.Item label="Observations">{report.observations}</Descriptions.Item>}
                {report.partyFeedback && <Descriptions.Item label="Party feedback">{report.partyFeedback}</Descriptions.Item>}
                {report.notes && <Descriptions.Item label="Notes">{report.notes}</Descriptions.Item>}
              </Descriptions>
            )}
          </Card>

          <Card title={`Expense items (${items.length})`} style={{ marginBottom: 16 }}
            extra={canEditItems ? <Button size="sm" variant="ghost" onClick={() => setEditTab('expenses')}>Edit expenses</Button> : undefined}>
            {items.length === 0 ? (
              <Typography.Text type="secondary">No line items on this report.</Typography.Text>
            ) : (
              <Table
                rowKey="id" dataSource={items} pagination={false} size="small" scroll={{ x: 'max-content' }} columns={lineColumns}
                footer={() => (
                  <div className="flex items-center justify-between text-content">
                    <b>Subtotal</b>
                    <b>{inr2(report.subtotalAmount)}</b>
                  </div>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Right: money, decisions, activity */}
        <Col xs={24} lg={9}>
          <Card title="Money" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Total spend (subtotal)"><b style={{ fontSize: 16 }}>{inr2(report.subtotalAmount)}</b></Descriptions.Item>
              {report.paidAmount != null && <Descriptions.Item label="Approved amount">{inr2(report.paidAmount)}</Descriptions.Item>}
            </Descriptions>
            <Button variant="text" style={{ paddingLeft: 0 }} onClick={() => navigate('/wallets')}>View Sales Person&apos;s wallet →</Button>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              Settled against the sales person&apos;s advance wallet — submitting debits their float; see the wallet for cash-in / cash-out history and current balance.
            </Typography.Paragraph>
          </Card>

          <Card title="Decisions" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Submitted">{submittedCell}</Descriptions.Item>
              <Descriptions.Item label={status === 'REJECTED' ? 'Rejected' : 'Approved'}>{approvedCell}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Activity">
            {activity.length ? <Timeline items={activity} /> : <Typography.Text type="secondary">No activity yet.</Typography.Text>}
          </Card>
        </Col>
      </Row>

      {/* ── Reason modal (send-back / reject / reopen) ── */}
      <Modal
        open={!!reasonModal}
        onOpenChange={(o) => { if (!o) { setReasonModal(null); setReason(''); } }}
        title={reasonModal?.title}
        footer={
          <>
            <Button variant="default" onClick={() => { setReasonModal(null); setReason(''); }}>Cancel</Button>
            <Button
              variant={reasonModal?.url.includes('reject') ? 'danger' : 'primary'}
              disabled={!reason.trim()}
              loading={action.isPending}
              onClick={() => {
                if (!reasonModal) return;
                action.mutate(
                  { url: reasonModal.url, data: { [reasonModal.field]: reason.trim() } },
                  { onSuccess: () => { setReasonModal(null); setReason(''); } },
                );
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (visible to the sales person)"
        />
      </Modal>

      {/* ── Approve modal (single approval layer) ── */}
      <Modal
        open={adminOpen}
        onOpenChange={(o) => { if (!o) setAdminOpen(false); }}
        title="Approve report"
        footer={
          <>
            <Button variant="default" onClick={() => setAdminOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={action.isPending}
              onClick={() =>
                action.mutate(
                  {
                    url: 'approve',
                    data: {
                      ...(approvedAmount != null ? { approvedAmount: Number(approvedAmount) } : {}),
                      ...(adminRemarks.trim() ? { remarks: adminRemarks.trim() } : {}),
                    },
                  },
                  { onSuccess: () => setAdminOpen(false) },
                )
              }
            >
              Approve
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Typography.Text type="secondary">Approved amount override (optional — defaults to subtotal {inr2(report.subtotalAmount)})</Typography.Text>
            <InputNumber
              style={{ width: '100%' }} min={0} value={approvedAmount}
              onChange={(v) => setApprovedAmount(v == null ? null : Number(v))}
              placeholder="Leave blank to approve full subtotal"
            />
          </div>
          <div>
            <Typography.Text type="secondary">Remarks (optional)</Typography.Text>
            <Input.TextArea rows={2} value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* ── Amount-mismatch verify modal ── */}
      <Modal
        open={!!mismatchItem}
        onOpenChange={(o) => { if (!o) setMismatchItem(null); }}
        title="Flag amount mismatch"
        footer={
          <>
            <Button variant="default" onClick={() => setMismatchItem(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!verifiedAmount || verifiedAmount <= 0}
              loading={verifyItem.isPending}
              onClick={() => {
                if (!mismatchItem) return;
                verifyItem.mutate({
                  itemId: String(mismatchItem.id),
                  data: {
                    status: 'AMOUNT_MISMATCH',
                    verifiedAmount: Number(verifiedAmount),
                    ...(mismatchReason.trim() ? { rejectionReason: mismatchReason.trim() } : {}),
                  },
                });
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography.Text type="secondary">
            Claimed amount: <b>{inr2(mismatchItem?.amount)}</b>
          </Typography.Text>
          <div>
            <Typography.Text type="secondary">Verified amount (₹)</Typography.Text>
            <InputNumber
              style={{ width: '100%' }} min={1} value={verifiedAmount}
              onChange={(v) => setVerifiedAmount(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <Typography.Text type="secondary">Reason (optional)</Typography.Text>
            <Input.TextArea rows={2} value={mismatchReason} onChange={(e) => setMismatchReason(e.target.value)} />
          </div>
        </div>
      </Modal>

      {editTab && (
        <EditReportModal
          reportId={id} initialTab={editTab} onClose={() => setEditTab(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['expense-report', id] }); qc.invalidateQueries({ queryKey: ['expense-reports'] }); }}
        />
      )}
    </div>
  );
}
